import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Box, DialogActions, DialogContent, Typography} from '@mui/material';
import {GridRowParams, GridSortModel, useGridApiRef} from '@mui/x-data-grid';
import {useDispatch, useSelector} from 'react-redux';
import {
  closeDialog,
  continueNCloseDialog,
  setOpenCount,
} from '../../../redux/slices/reportsDialogSlice';
import AurClientDropdown from '../../client-management/aur-client-dropdown/AurClientDropdown';
import AurDataGrid from '../../common/aur-data-grid/AurDataGrid';
import AurPagination from '../../common/aur-pagination/AurPagination';
import {getWorkspaceDatasetByWorkspaceId} from '../../../api/ReportingManagement';
import {getUTCDateTimeForDisplay} from '../../../utils/utils';
import {RootState} from '../../../redux/store';
import {
  setCurrentReportTypeTab,
  storeSelectedDataSet,
} from '../../../redux/slices/reportsSlice';
import {useNavigate, useSearchParams} from 'react-router-dom';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import AurDataGridNoRowsOverlay from '../../common/aur-data-grid-no-rows-overlay/AurDataGridNoRowsOverlay';
import {ReactComponent as RadioCheckedIcon} from '../../../assests/svg/radio-checked.svg';
import {ReactComponent as RadioUnCheckedIcon} from '../../../assests/svg/radio-unchecked.svg';
import {t} from 'i18next';
import {
  PROJECT_DROP_DOWN_ID,
  TABLE_PAGE_SIZE_OPTION,
  TABLE_PER_PAGE_DEFAULT_50_ROW,
  TABLE_PER_PAGE_DEFAULT_NO_ROW,
  WORKSPACE_DROP_DOWN_ID,
} from '../../../constants/constants';
import AurSearchBox from '../../common/aur-search-box/AurSearchBox';
import {CustomGridColDef} from '../../common/aur-data-grid-pro/AurDataGridPro';
import {AurLightTooltip} from '../../common/aur-light-tooltip/aur-light-tooltip';
import {
  GA_CLICK_TYPES,
  GA_DATA_MODE_AUTO,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../constants/analyticsConstants';
import {setReportSrc} from '../../../redux/slices/analyticsSlice';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {v4 as uuid} from 'uuid';
import AurDataGridErrorOverlay from '../../common/aur-data-grid-error-overlay/AurDataGridErrorOverlay';
import {selectOption} from '../../../redux/slices/clientSlice';

interface ProjectWorkSpaceProps {
  heading: string;
  subHeading: string;
  firstDropDownData: any[];
  secondDropDownData: any[];
  setParentCloseCB?: (cb: () => void) => void;
  isOpenInDialog?: boolean;
}

interface DataSetType {
  id: string;
  datasetName: string;
  processingCompletedOn: string;
  mediaId: number;
  custodianName: string;
  custodianId: number;
  evidenceSource: string;
}

type AurDataGridColDef = CustomGridColDef & {
  isSearchBoxRequired?: boolean;
  isBorderNotRequired?: boolean;
};
const COMMON_ACCESSIBILITY_STRING = 'reports.selectionModal.accessibility.';
const COLUMN_HEIGHT = 64;

const ProjectWorkspace = (props: ProjectWorkSpaceProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {trackGAEvent} = useAnalytics();
  const gridAPIRef = useGridApiRef();
  const [searchParams] = useSearchParams();

  const dialogOpenCount = useSelector(
    (state: RootState) => state.dialog
  ).openCount;

  const {
    clientId,
    projectId: initialProjectId,
    workspaceId: initialWorkspaceId,
    workspaceDataSetName,
    workspaceDataSetCustodianId,
    previousReportTypeTab,
    pageOpenFrom: reportPageOpenFrom,
  } = useSelector((state: RootState) => state.reports);

  useEffect(() => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
      subPage: {
        subpage_name: heading,
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
      user: GA_DATA_MODE_AUTO,
    });
    if (props.setParentCloseCB) {
      props.setParentCloseCB(handleCancelClick);
    }
  }, []);

  const handleContinueClick = () => {
    const dataSetDetail = workspaceDatasets.find(
      (item: DataSetType) => item.id === selectedDataset?.id
    );
    if (selectedDataset && dataSetDetail && projectId && workSpaceId) {
      const selectedProjectName = projects.selectedOptionLable;
      const selectedWorkSpaceName = workSpaces.selectedOptionLable;
      dispatch(
        storeSelectedDataSet({
          projectId: projectId,
          workspaceId: workSpaceId,
          projectName: selectedProjectName ?? '',
          workspaceName: selectedWorkSpaceName ?? '',
          workspaceDataSetName: dataSetDetail?.datasetName ?? '',
          worksapceDataSetMediaId: dataSetDetail?.mediaId
            ? dataSetDetail.mediaId.toString()
            : '',
          wokrspaceDataSetCustodianId: dataSetDetail?.custodianId,
        })
      );
    }
    dispatch(continueNCloseDialog());
    dispatch(setOpenCount(2));
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.global.continue'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.MODAL,
        action: t('gaTracking.reports.changedWorkspaceAndDataset'),
      },
      subPage: {
        subpage_name: heading,
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
  };

  const handleCancelClick = () => {
    if (workspaceDataSetName) {
      dispatch(closeDialog());
      return;
    }
    if (dialogOpenCount === 1) {
      dispatch(setReportSrc(GA_REGIONS.BODY));
      dispatch(setCurrentReportTypeTab(undefined));
      navigate(reportPageOpenFrom ?? '/welcome-single-client', {replace: true});
      dispatch(closeDialog());
      return;
    }
    if (!workspaceDataSetName) {
      dispatch(setReportSrc(GA_REGIONS.BODY));
      dispatch(setCurrentReportTypeTab(previousReportTypeTab));
      if (previousReportTypeTab) {
        searchParams.set('tab', previousReportTypeTab);
        navigate(
          {
            pathname: location.pathname,
            search: searchParams.toString(),
          },
          {replace: true}
        );
      }
    }
    dispatch(closeDialog());
  };

  const focusContinueButton = () => {
    setTimeout(() => {
      const button = document.querySelector(
        '[data-testid="pagination-continue-button"]'
      );
      if (button) {
        (button as HTMLButtonElement).focus();
      }
    }, 200);
  };

  const {heading, subHeading, firstDropDownData, secondDropDownData} = props;
  /** Storing selected dataset's name and its row id.
   * row id generated from FE side as datagrid required unique id
   */
  const [selectedDataset, setSelectedDataset] = useState<{
    id: string;
    dataset: string;
  } | null>();
  const [workspaceDatasets, setWorkspaceDatasets] = useState<DataSetType[]>([]);
  const [workspaceDatasetsCount, setWorkspaceDatasetsCount] = useState(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(
    TABLE_PER_PAGE_DEFAULT_50_ROW
  );
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [haveReqError, setHaveReqError] = useState(false);
  // To ignore the dataset clear in specific use case when search, pagination or project change
  const ignoreSelectedDatasetClear = useRef<boolean>(false);
  /**
   * To track initial first data set list is loaded when dialog open
   * when totaldata set is more than 50 so by default we need to load 50 data per page
   * so this variable used to track this case
   */
  const isInitialDatasetLoaded = useRef<boolean>(false);
  // to track preselected dataset is assign when dialog open
  const isPreselectedDatasetAssigned = useRef<boolean>(false);
  const isResetItemPerPageRef = useRef<boolean>(false);

  const onSetSortModel = (sortModel: GridSortModel) => {
    setSortModel(sortModel);
  };

  const continuButtonDisabled = useMemo(() => {
    if (selectedDataset) return false;
    return true;
  }, [selectedDataset]);

  const handleRadioChange = (dataset: DataSetType) => {
    setSelectedDataset({
      id: dataset.id,
      dataset: dataset.datasetName,
    });
    focusContinueButton();
  };

  const projects = useSelector(
    (state: RootState) => state.clientReducer[PROJECT_DROP_DOWN_ID]
  );
  const workSpaces = useSelector(
    (state: RootState) => state.clientReducer[WORKSPACE_DROP_DOWN_ID]
  );

  const [projectId, setProjectId] = useState(initialProjectId);
  const [workSpaceId, setWorkSpaceId] = useState(initialWorkspaceId);

  useEffect(() => {
    setProjectId(projects?.selectedOption);
  }, [projects?.selectedOption]);

  useEffect(() => {
    setWorkSpaceId(workSpaces?.selectedOption);
  }, [workSpaces?.selectedOption]);

  useEffect(() => {
    /**
     * Pagination component added at footer here so AurDataGrid ref not getting sync with it that's why
     * grid always show only 25 record even loaded 50 record so need to sync it
     */
    gridAPIRef.current.setPage(0);
    gridAPIRef.current.setPageSize(itemsPerPage);
  }, [itemsPerPage]);

  useEffect(() => {
    gridAPIRef.current.setPage(currentPage - 1);
  }, [currentPage]);

  useEffect(() => {
    setSelectedDataset(null);
  }, [projectId, workSpaceId]);

  const fetchUsers = async () => {
    if (projectId && workSpaceId && clientId) {
      if (isLoading) return;

      if (isResetItemPerPageRef.current) {
        isResetItemPerPageRef.current = false;
        return;
      }

      setIsLoading(true);
      setHaveReqError(false);
      try {
        const paramString = `?SearchTerm=${searchTerm}&SortColumn=${sortModel[0]?.field ?? ''}&SortOrder=${sortModel[0]?.sort ?? 'asc'}&clientId=${clientId}&projectId=${projectId}&pageSize=${itemsPerPage}&pageNumber=${currentPage}`;
        const result = await getWorkspaceDatasetByWorkspaceId(
          workSpaceId,
          paramString
        );
        let dataSets: DataSetType[] = (result?.data?.data ?? []).map(
          (item: object) => ({
            ...item,
            id: uuid(),
          })
        );

        const datasetCount = result?.data?.meta?.pagination?.totalItems;

        if (
          !isInitialDatasetLoaded.current &&
          datasetCount < TABLE_PER_PAGE_DEFAULT_50_ROW
        ) {
          ignoreSelectedDatasetClear.current = true;
          dataSets = dataSets.splice(0, TABLE_PER_PAGE_DEFAULT_NO_ROW);
          isResetItemPerPageRef.current = true;
          setTimeout(() => {
            setItemsPerPage(TABLE_PER_PAGE_DEFAULT_NO_ROW);
          }, 100);
        }

        if (
          workspaceDataSetName &&
          !selectedDataset &&
          !isPreselectedDatasetAssigned.current
        ) {
          // to Track initial selected dataset assign is completed so don't execute for next data load call
          isPreselectedDatasetAssigned.current = true;
          const selectedRow = dataSets.find(
            (item: DataSetType) =>
              item.datasetName === workspaceDataSetName &&
              item.custodianId === workspaceDataSetCustodianId
          );
          if (selectedRow) {
            setSelectedDataset({
              id: selectedRow.id,
              dataset: selectedRow.datasetName,
            });
          }
        }
        setWorkspaceDatasets(dataSets);

        // Make it initial Dataset true as first dataload call is completed.
        isInitialDatasetLoaded.current = true;
        setWorkspaceDatasetsCount(datasetCount);
        setIsLoading(false);
      } catch (error) {
        setHaveReqError(true);
        setIsLoading(false);
        console.error('Error during API call:', error);
      }
    } else {
      setWorkspaceDatasets([]);
    }
  };

  useEffect(() => {
    if (!ignoreSelectedDatasetClear.current) {
      setSelectedDataset(null);
    }
    ignoreSelectedDatasetClear.current = false;
    fetchUsers();
  }, [
    searchTerm,
    sortModel,
    itemsPerPage,
    currentPage,
    projectId,
    workSpaceId,
  ]);

  const columns: AurDataGridColDef[] = [
    {
      field: 'datasetName',
      headerName: t('reports.selectionModal.datasetName'),
      headerClassName: 'custom-header-report-grid',
      flex: 1,
      sortable: true,
      renderCell: (params) => {
        const isSelected =
          selectedDataset?.dataset === params.row.datasetName &&
          selectedDataset?.id === params.row.id;
        return (
          <div className="row-container">
            <div
              className="img-container"
              style={{cursor: 'pointer'}}
              onClick={() => handleRadioChange(params.row as DataSetType)}
            >
              {isSelected ? <RadioCheckedIcon /> : <RadioUnCheckedIcon />}
            </div>
            <div>
              <Typography className="typo-text body3_size list-text">
                {params.value}
              </Typography>
            </div>
          </div>
        );
      },
    },
    {
      field: 'evidenceSource',
      headerName: t('reports.selectionModal.evidenceSource'),
      headerClassName: 'custom-header',
      flex: 0.8,
      sortable: false,
      renderCell: (params) => {
        return (
          <Box className="summary-exception-table-cell">{params.value}</Box>
        );
      },
    },
    {
      field: 'custodianName',
      headerName: t('reports.selectionModal.custodian'),
      headerClassName: 'custom-header',
      flex: 0.8,
      sortable: false,
      renderCell: (params) => {
        return (
          <Box className="summary-exception-table-cell">
            <AurLightTooltip children={params.value} title={params.value} />
          </Box>
        );
      },
    },
    {
      field: 'processingCompletedOn',
      headerName: t('reports.selectionModal.dateCompletedProcessing'),
      headerClassName: 'custom-header',
      flex: 1.2,
      sortable: false,
      isBorderNotRequired: true,
      isDateColumn: true,
      renderCell: (params) => {
        return (
          <div className="row-container">
            <Typography
              className="typo-text body3_size list-text"
              paddingLeft="5px"
            >
              {getUTCDateTimeForDisplay(params.value)}
            </Typography>
          </div>
        );
      },
    },
  ];

  const setItemsPerPageHandler = (itemPerPage: number) => {
    setItemsPerPage(itemPerPage);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const onSearch = (searchTerm: string) => {
    setCurrentPage(1);
    if (searchTerm.length > 3) {
      setSearchTerm(searchTerm);
    } else {
      setSearchTerm('');
    }
  };

  const perPageOptions = useMemo(() => {
    const options = TABLE_PAGE_SIZE_OPTION.filter(
      (option) => option <= workspaceDatasetsCount
    );
    const additionalOption = TABLE_PAGE_SIZE_OPTION.find(
      (option) => option >= workspaceDatasetsCount
    );
    if (additionalOption && !options.includes(additionalOption)) {
      options.push(additionalOption);
    }
    return options;
  }, [workspaceDatasetsCount]);

  const onProjectChange = useCallback(
    (selectedProjectId: string) => {
      if (selectedProjectId !== projectId) {
        setCurrentPage(1);
        dispatch(
          selectOption({
            dropdownId: WORKSPACE_DROP_DOWN_ID,
            selectedOption: null,
            selectedOptionLable: null,
          })
        );
      }
    },
    [dispatch, projectId]
  );

  const onWorkspaceChange = useCallback(
    (selectedWorkspaceId: string) => {
      if (selectedWorkspaceId !== workSpaceId) {
        setCurrentPage(1);
      }
    },
    [workSpaceId]
  );

  const AurDataGridLoadingOverlay = () => {
    if (!isLoading && workspaceDatasets.length === 0)
      return <AurDataGridNoRowsOverlay />;

    if (isLoading)
      return (
        <div className="grid-loading">
          <AurLoadingIndicator />
        </div>
      );
    return <div />;
  };
  return (
    <>
      <DialogContent className="model-content">
        <div className={`project-workspace ${haveReqError && 'flex-one'}`}>
          <Typography className="typo-text headline3_size heading">
            {heading}
          </Typography>
          <Typography className="typo-text body2_size subheading">
            {subHeading}
          </Typography>
          <div className="dropdown-main-div">
            <AurClientDropdown
              buttonOrTextField={'text'}
              data={firstDropDownData}
              buttonLabel={t('reports.selectionModal.project')}
              dropDownID={PROJECT_DROP_DOWN_ID}
              idKey="projectId"
              displayKey="projectName"
              enableSearch={true}
              initialSelectedOptionId={initialProjectId ?? undefined}
              buttonBorderHighlight={true}
              onOptionPick={onProjectChange}
              ariaLabel={t('reports.selectionModal.project')}
            />
            <AurClientDropdown
              buttonOrTextField={'text'}
              data={secondDropDownData}
              buttonLabel={t('reports.selectionModal.workspace')}
              dropDownID={WORKSPACE_DROP_DOWN_ID}
              idKey="workspaceId"
              displayKey="name"
              enableSearch={true}
              selectDefaultFirstItem={false}
              initialSelectedOptionId={initialWorkspaceId ?? undefined}
              buttonBorderHighlight={true}
              onOptionPick={onWorkspaceChange}
            />
          </div>
          <AurSearchBox
            className="dataset-table-search"
            onSearch={onSearch}
            searchLoading={searchTerm.trim().length > 0 && isLoading}
            inputDataTestId="search-input"
          />
          <div
            className={`dataset-table-container ${!haveReqError ? 'flex-one' : ''}`}
          >
            <AurDataGrid
              apiRef={gridAPIRef}
              loading={isLoading}
              className={`reports-grid summary-exception-data-table ${haveReqError && 'dataset-listing-table-fix-height'}`}
              rows={workspaceDatasets}
              columns={columns}
              pageSizeOptions={perPageOptions}
              checkBoxVisible={false}
              visibleToolTip={true}
              entityType={t('common.reportsModel')}
              getRowHeight={() => 56}
              columnHeaderHeight={COLUMN_HEIGHT}
              pointer={true}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              showPagination={false}
              defaultSortColumnName="datasetName"
              primaryKey="id"
              sortModel={sortModel}
              setSortModel={onSetSortModel}
              noRowsOverlay={AurDataGridLoadingOverlay}
              rowSelection
              onRowClick={(params: GridRowParams) =>
                handleRadioChange(params.row)
              }
              rowSelectionModel={selectedDataset ? [selectedDataset.id] : []}
              headerSpanTabIndex={-1}
              defaultHeaderCellTabIndex={-1}
              disableHeaderRowTabIndex
              aria-live="polite"
              aria-label={t(COMMON_ACCESSIBILITY_STRING + 'datasetTable')}
              enableRowClickOnEnterPress
              addedInDialog
              classNameOfParentScollElement="dataset-table-container" // Scrolling manage by element of this class
            />
          </div>
          {haveReqError && (
            <div className="table-error-container">
              <AurDataGridErrorOverlay
                enableParentWrapper={false}
                containerClass="error-container"
                onRefresh={fetchUsers}
              />
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions className="footer-action-container">
        <AurPagination
          totalItems={workspaceDatasetsCount}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          perPageOptions={perPageOptions}
          itemType={t('common.reports')}
          setItemsPerPage={setItemsPerPageHandler}
          onPageChange={handlePageChange}
          includeCancelContinueEvents={true}
          handleContinueClick={handleContinueClick}
          handleCancelClick={handleCancelClick}
          continuButtonDisabled={continuButtonDisabled}
          backgroundColor="#0C1119"
          canClearAriaLabel={t(COMMON_ACCESSIBILITY_STRING + 'cancelButton', {
            screen:
              dialogOpenCount == 1
                ? t(COMMON_ACCESSIBILITY_STRING + 'welcomeClient')
                : t(COMMON_ACCESSIBILITY_STRING + 'processingInProgressTab'),
          })}
          continueButtonHint={t(
            `reports.selectionModal.accessibility.${continuButtonDisabled ? 'makeContinueButtonEnable' : 'continueButton'}`
          )}
          trackPaginationActionName={t('gaTracking.global.reportPagination')}
        />
      </DialogActions>
    </>
  );
};

export default ProjectWorkspace;
